import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { PayrollService } from '@/services/payroll.service';
import { format } from 'date-fns';
import { Deductions } from './payroll/Deductions';

export const Payroll: React.FC = () => {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as 'payroll' | 'benefits' | 'deductions' | 'taxforms') || 'payroll';
  const [activeTab, setActiveTab] = useState<'payroll' | 'benefits' | 'deductions' | 'taxforms'>(initialTab);

  React.useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['payroll', 'benefits', 'deductions', 'taxforms'].includes(tab)) {
      setActiveTab(tab as 'payroll' | 'benefits' | 'deductions' | 'taxforms');
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'payroll' | 'benefits' | 'deductions' | 'taxforms') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const { data: payrollRecords = [], isLoading: payrollLoading } = useQuery({
    queryKey: ['payrollRecords'],
    queryFn: () => PayrollService.getAllPayrollRecords(),
    enabled: !!profile && activeTab === 'payroll',
  });

  const { data: benefits = [], isLoading: benefitsLoading } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => PayrollService.getAllBenefits(),
    enabled: !!profile && activeTab === 'benefits',
  });

  const tabs = [
    { id: 'payroll' as const, name: 'Payroll Records', count: payrollRecords.length },
    { id: 'benefits' as const, name: 'Benefits', count: benefits.length },
    { id: 'deductions' as const, name: 'Deductions', count: 0 },
    { id: 'taxforms' as const, name: 'Tax Forms', count: 0 },
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      processing: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate totals
  const totalGrossPay = payrollRecords.reduce((sum, r) => sum + r.grossPay, 0);
  const totalNetPay = payrollRecords.reduce((sum, r) => sum + r.netPay, 0);
  const totalDeductions = payrollRecords.reduce((sum, r) => sum + r.totalDeductions, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Payroll Management</h1>
        <p className="mt-2 text-gray-600">Manage payroll, benefits, deductions, and tax forms</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Records</div>
          <div className="text-2xl font-bold text-gray-900">{payrollRecords.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Gross Pay</div>
          <div className="text-2xl font-bold text-green-600">${totalGrossPay.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Deductions</div>
          <div className="text-2xl font-bold text-red-600">${totalDeductions.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Net Pay</div>
          <div className="text-2xl font-bold text-blue-600">${totalNetPay.toLocaleString()}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Payroll Records Tab */}
      {activeTab === 'payroll' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Payroll Records</h2>
            <Link
              to="/payroll/records/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Process Payroll
            </Link>
          </div>

          {payrollLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading payroll records...</p>
            </div>
          ) : payrollRecords.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No payroll records found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {payrollRecords.map((record) => (
                  <li key={record.id}>
                    <Link
                      to={`/payroll/records/${record.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {record.employeeName}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                              {record.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Pay Date: {format(record.payDate, 'MMM d, yyyy')} • Period: {format(record.periodStart, 'MMM d')} - {format(record.periodEnd, 'MMM d')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            Gross: ${record.grossPay.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-600">
                            Net: ${record.netPay.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Employee Benefits</h2>
            <Link
              to="/payroll/benefits/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Add Benefit
            </Link>
          </div>

          {benefitsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading benefits...</p>
            </div>
          ) : benefits.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No benefits found</p>
              <Link
                to="/payroll/benefits/new"
                className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                + Add Your First Benefit
              </Link>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {benefits.map((benefit) => (
                  <li key={benefit.id}>
                    <Link
                      to={`/payroll/benefits/${benefit.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {benefit.employeeName} - {benefit.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              benefit.status === 'active' ? 'bg-green-100 text-green-800' :
                              benefit.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {benefit.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {benefit.planName || benefit.provider || 'No plan/provider'} • {benefit.coverageLevel}
                            {benefit.effectiveDate && ` • Effective: ${format(benefit.effectiveDate, 'MMM d, yyyy')}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            ${benefit.totalCost.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            Employee: ${benefit.employeeContribution.toLocaleString()} | Employer: ${benefit.employerContribution.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Deductions Tab */}
      {activeTab === 'deductions' && (
        <div>
          <Deductions />
        </div>
      )}

      {/* Tax Forms Tab */}
      {activeTab === 'taxforms' && (
        <div>
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600">Tax forms management coming soon...</p>
          </div>
        </div>
      )}
    </div>
  );
};

